import React, { useCallback } from 'react';
import {
  getLanguage,
  PENSIEVE_TOOLS_DRAW_ERROR,
  type PensieveQuestion,
  type PensieveQuestionLevel,
  type PensieveToolsDrawAck,
} from '@deadpan/shared';
import { useTranslations } from '../hooks/useTranslations';
import { useSocket } from '../contexts/SocketContext';
import { usePersistedPensieveLevelRange } from '../hooks/usePersistedPensieveLevelRange';

interface PensieveProps {
  /** When true, used inside modal (no card wrapper, compact header) */
  embedded?: boolean;
}

const LEVELS: PensieveQuestionLevel[] = [0, 1, 2, 3];

export const Pensieve: React.FC<PensieveProps> = ({ embedded = false }) => {
  const t = useTranslations();
  const { socket } = useSocket();
  const { range, setMin, setMax } = usePersistedPensieveLevelRange();
  const [result, setResult] = React.useState<PensieveQuestion | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const isZh = getLanguage() === 'zh';

  const requestQuestionDraw = useCallback(async (): Promise<PensieveToolsDrawAck | null> => {
    if (!socket) return null;
    return await new Promise<PensieveToolsDrawAck>((resolve) => {
      socket.emit('client:pensieve-tools-draw', range, (ack: PensieveToolsDrawAck) => resolve(ack));
    });
  }, [socket, range]);

  const handleDraw = useCallback(async () => {
    setError(null);
    if (!socket) {
      setError(isZh ? '请先连接服务器' : 'Connect to the server first');
      setResult(null);
      return;
    }

    setIsDrawing(true);
    try {
      const ack = await requestQuestionDraw();
      if (!ack) {
        setError(isZh ? '抽题失败，请重试' : 'Failed to draw a question. Please try again.');
        setResult(null);
        return;
      }

      if (!ack.ok) {
        if (ack.code === PENSIEVE_TOOLS_DRAW_ERROR.NO_QUESTIONS) {
          setError(isZh ? '该区间内暂无可用问题' : 'No questions available in this range');
        } else {
          setError(isZh ? '抽题失败，请重试' : 'Failed to draw a question. Please try again.');
        }
        setResult(null);
        return;
      }

      const question = ack.question;
      setResult(question);
    } finally {
      setIsDrawing(false);
    }
  }, [isZh, socket, range, requestQuestionDraw]);

  const wrapperClass = embedded
    ? 'max-w-md'
    : 'bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20';

  return (
    <div className={wrapperClass}>
      <div className={embedded ? 'mb-3' : 'mb-4 sm:mb-6'}>
        {!embedded && (
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-2">
            {t.ui.pensieveTitle}
          </h2>
        )}
        <p className="text-xs sm:text-sm text-gray-600">{t.ui.pensieveDescription}</p>
      </div>
      <div className="space-y-4">
        <div>
          <p className="text-xs sm:text-sm text-gray-600 mb-3">
            {t.ui.pensieveRule1}
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center items-stretch gap-3 flex-wrap">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="pensieveLevelMin" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                {t.ui.pensieveLevelMinLabel}
              </label>
              <select
                id="pensieveLevelMin"
                value={range.min}
                onChange={(e) => {
                  setMin(Number(e.target.value) as PensieveQuestionLevel);
                  setResult(null);
                  setError(null);
                }}
                className="w-full sm:w-24 px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all outline-none bg-white"
              >
                {LEVELS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="pensieveLevelMax" className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                {t.ui.pensieveLevelMaxLabel}
              </label>
              <select
                id="pensieveLevelMax"
                value={range.max}
                onChange={(e) => {
                  setMax(Number(e.target.value) as PensieveQuestionLevel);
                  setResult(null);
                  setError(null);
                }}
                className="w-full sm:w-24 px-4 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all outline-none bg-white"
              >
                {LEVELS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        {error && (
          <div className="text-red-700 text-sm bg-red-50 border border-red-200 p-3 rounded-xl">
            {error}
          </div>
        )}
        {result !== null && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
            <p className="text-lg sm:text-xl font-semibold text-purple-700 break-words">
              {isZh ? result.questionZh : result.question}
            </p>
          </div>
        )}
        <button
          type="button"
          onClick={handleDraw}
          disabled={isDrawing}
          className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          🔮 {t.ui.pensieveButton}
        </button>
      </div>
    </div>
  );
};
